import { ComponentInternalInstance, getCurrentInstance, reactive, ref, Ref, UnwrapRef } from '@vue/composition-api';
import { RowEventContext } from 'tdesign-vue';
import _ from 'lodash';

// 引入工具类
import { dragFileHandler, DragWidgetSize } from '@/renderer/components/SpacePage/compositions/dragFileHandler';
import { IS_OPEN_IN_MAC } from '@/common/constant/platform';
import { SMH } from '@/renderer/scripts/smh';
import ipc from '@/renderer/scripts/utils/ipc';

enum DroppableStatus {
  // 可以drop
  Droppable,
  // 非文件夹类型不可以drop
  FileTypeNotDroppable,
  // dragging的file不可以drop
  DraggingFileNotDroppable,
  // 不在dragging过程
  NotDragging,
}

// table数据依赖于listHandler，且需命名一致
export type TableData = {
  list: any[];
  NextMarker?: number;
  crumbs?: string[];
  selectedRowKeys: any[];
  selectedList: any[];
  authorityList?: SMH.AuthorityList;
  canRestoreFiles?: any[];
  canDeleteFiles?: any[];
  runningList?: any[];
  searchId?: string;
  expire?: number;
  hasMore?: boolean;
  total?: number;
};
type Handler = {
  operateShowMap: UnwrapRef<Record<string, Boolean>>;
  isDropdownShow: Ref<boolean>;
  isActionFromMoreBtn: Ref<boolean>;
  isFirstButtonHover: Ref<boolean>;
  isSecondButtonHover: Ref<boolean>;
  isThirdButtonHover: Ref<boolean>;
  isFourthButtonHover: Ref<boolean>;
  isFifthButtonHover: Ref<boolean>;
  isSixthButtonHover: Ref<boolean>;
  hoverIndex: Ref<number>;
  spaceId: Ref<SpaceId>;
  isShowMask: Ref<boolean>;
  maskWidth: Ref<number>;
  maskHeight: Ref<number>;
  maskLeft: Ref<number>;
  maskTop: Ref<number>;
  moveFileTask: Ref<{
    sourceFile: any[];
    moveToPath: string;
  }>;
  isDragging: Ref<boolean>;
  droppableStatus: Ref<DroppableStatus>;
  methods: {
    handleClickRow: (context: RowEventContext<any>) => void;
    handleRowMouseDown: (context: RowEventContext<any>) => void;
    handleMouseDown: (context: RowEventContext<any>) => void;
    handleMouseUp: (context: RowEventContext<any>) => void;
    handleMouseUpOnBreadCrumb: (item: ListBreadcrumb, index?: number) => void;
    handleSelectRows: (value: Array<string>, opt: any) => void;
    handleSelectRowsByIndex: (value: Array<number>) => void;
    rightClickCallback: (event: any) => boolean;
    menuShowCallback: (bShow: any) => void;
    handleEllipsisClick: (row: any) => void;
    handleEnterRow: (context: RowEventContext<any>) => void;
    handleLeaveRow: (context: RowEventContext<any>) => void;
    onMouseEnterEvent: (command: string) => void;
    onMouseLeaveEvent: (command: string) => void;
    onScroll: (params: { e: WheelEvent }) => void;
    onClickOutside: () => void;
    isContextMenuShow: () => boolean;
    onMouseDownEvent: (event: MouseEvent) => boolean;
    onMouseMoveEvent: (event: MouseEvent) => boolean;
    onMouseUpEvent: (event: MouseEvent) => boolean;
    handleShiftClick: (index: number) => boolean;
    getShiftStartIndex: () => void;
    handleCtrlClick: (selectedIndex: number, row: any) => void;
    handleEscClick: () => void;
  };
};
export default function listOperateHandler(tableData: TableData, enableMutiSelect = false): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const operateShowMap = reactive<Record<string, Boolean>>({}); // 控制行悬浮的显示
  const isDropdownShow = ref(false); // 用来判断是否点击更多按钮
  const isActionFromMoreBtn = ref(false); // 判断菜单展示是否来源于更多按钮弹出的
  const isFirstButtonHover = ref(false); // 用来判断是否移入第一个按钮
  const isSecondButtonHover = ref(false); // 用来判断是否移入第二个按钮
  const isThirdButtonHover = ref(false); // 用来判断是否移入第三个按钮
  const isFourthButtonHover = ref(false); // 用来判断是否移入第四个按钮
  const isFifthButtonHover = ref(false); // 用来判断是否移入第五个按钮
  const isSixthButtonHover = ref(false); // 用来判断是否移入第六个按钮

  const hoverIndex = ref(-1); // 用来控制移入的行号
  const preItemName = ref('');
  const spaceId = ref('');

  // 控制框选的变量
  const maskWidth = ref(0);
  const maskHeight = ref(0);
  const maskLeft = ref(0);
  const maskTop = ref(0);
  const isShowMask = ref(false);
  let startX = 0;
  let startY = 0;
  let endX = 0;
  let endY = 0;
  let domMask: Element | null | undefined; // 框选蒙版的element
  let tableListRect: DOMRect | undefined; // 表格的rect
  let listRowClass: Array<Element> = []; // 当前页的所有rowitem
  let listRowClassCache: Array<DOMRect> = []; // 可见区域rowitem，用于加速计算
  let listRowTagetCache: HTMLElement | undefined;
  let listRowCacheIndex = 0; // 可见区域首个rowitem的index
  let isMarqueeSelectItem = false; // 是否已经触发了框选选中item,用于和点击做区分，点击一个选中item需要触发取消选中，框选一个item不触发取消选中
  let isSelectDownBengin = false; // 用于优化点击效率，把相关变量初始化推迟到mousemove
  let isRowMouseDown = false; // 用于加速计算点击空白处取消item选中功能

  let isMenuShow = false;
  let shift_start_index = -1;
  const isDragging = ref(false);
  let dragMouseDownEventContext: RowEventContext<any> | null;
  const moveFileTask: Ref<{
    sourceFile: any[];
    moveToPath: string;
  }> = ref({
    sourceFile: [],
    moveToPath: '',
  });
  const droppableStatus = ref<DroppableStatus>(DroppableStatus.Droppable);
  const dragMouseMoveHandler = (moveEvent: MouseEvent) => {
    if (!isDragging.value) {
      if (!dragMouseDownEventContext) {
        return;
      }
      const { index, row, e } = dragMouseDownEventContext;

      // 防抖
      if (Math.abs(e.clientX - moveEvent.clientX) < 5 && Math.abs(e.clientY - moveEvent.clientY) < 5) {
        return;
      }
      const mainDraggingFile = tableData.list[index];
      let widgetSize: DragWidgetSize | undefined;
      listRowClass = Array.from(document.querySelectorAll('.listRowClassName'));
      if (listRowClass.length > 0) {
        widgetSize = {
          width: listRowClass[0].clientWidth,
          height: listRowClass[0].clientHeight,
        };
      }
      dragFileHandler.fillInfo(mainDraggingFile, tableData.selectedList, tableData.selectedRowKeys, widgetSize);
      isDragging.value = true;
    }
    dragFileHandler.showDragViewAt({
      x: moveEvent.clientX,
      y: moveEvent.clientY,
    });
  };
  const dragMouseUpHandler = (e: MouseEvent) => {
    if (isDragging.value) {
      window.removeEventListener('mousemove', dragMouseMoveHandler);
      window.removeEventListener('mouseup', dragMouseUpHandler);
      dragFileHandler.hideDragView();
    }
    isDragging.value = false;
    droppableStatus.value = DroppableStatus.NotDragging;
    dragMouseDownEventContext = null;
  };
  const methods = {
    handleClickRow(context: RowEventContext<any>) {
      const { index, row, e } = context;
      const { target, button, type, ctrlKey, shiftKey, metaKey } = e;
      if (isMarqueeSelectItem) {
        isMarqueeSelectItem = false;
        return;
      }

      // 可疑文件模块，如果禁用直接return，不选中
      if (row.inDirectory === false) return;
      const { className, parentElement } = target as HTMLElement;
      // 更新spaceId
      spaceId.value = row.spaceId; // 目前与我相关页面独有
      // isDropdownShow.value = false;
      hoverIndex.value = index;
      ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_FILTER, [row]); // 误删恢复独有
      // 判断是否切换行
      if (preItemName.value !== row.name) {
        if (preItemName.value) {
          vm.$set(operateShowMap, preItemName.value, false);
        }
        preItemName.value = row.name;
        vm.$set(operateShowMap, row.name, true);
      }

      // 点击下拉菜单中选择项 || 点击操作栏中按钮时，仅选中所在行
      if ((typeof className === 'string' && className.includes('dropdown')) || className === 'link') {
        tableData.selectedRowKeys = [];
        tableData.selectedList = [];
        isDropdownShow.value = false;
        vm.$set(operateShowMap, row.name, false);
      }
      if (
        target &&
        ((typeof className === 'string' && (className.includes('t-checkbox') || className.includes('mr'))) ||
          typeof className === 'object')
      )
        return;
      if (row.type === 'dir' && className?.includes('file-name')) return;
      let selectedIndex = tableData.selectedRowKeys.findIndex(id => id === row.id);
      // 当右键按下
      if (button === 2) {
        if (tableData.selectedRowKeys.length > 1 && selectedIndex !== -1) {
          // 在多选区域右键，直接返回
          return;
        }
        tableData.selectedRowKeys = [];
        tableData.selectedList = [];
        isActionFromMoreBtn.value = false;
        isDropdownShow.value = false;
        selectedIndex = -1;
        vm.$set(operateShowMap, row.name, false);
      }

      // 实现shift选中
      if (enableMutiSelect && shiftKey && shift_start_index !== -1) {
        methods.handleShiftClick(index);
        return;
      }
      shift_start_index = index;
      if (enableMutiSelect && ((IS_OPEN_IN_MAC && metaKey) || (ctrlKey && !IS_OPEN_IN_MAC))) {
        methods.handleCtrlClick(selectedIndex, row);
      } else if (selectedIndex > -1) {
        // 多选点击单个默认选中
        if (tableData.selectedRowKeys.length > 1) {
          tableData.selectedRowKeys = [row.id];
          tableData.selectedList = [row];
        } else {
          tableData.selectedRowKeys = [];
          tableData.selectedList = [];
        }
      } else {
        tableData.selectedRowKeys = [row.id];
        tableData.selectedList = [row];
      }
    },
    handleMouseDown(context: RowEventContext<any>) {
      const { e } = context;
      const { button } = e;
      methods.handleRowMouseDown(context);
      if (button === 0) {
        methods.hanldeLeftMouseDown(context);
      }
    },
    hanldeLeftMouseDown(context: RowEventContext<any>) {
      const { index } = context;
      if (isDropdownShow.value) return;
      if (tableData.selectedRowKeys.includes(index)) {
        dragMouseDownEventContext = context;
        window.addEventListener('mousemove', dragMouseMoveHandler);
        window.addEventListener('mouseup', dragMouseUpHandler);
      }
    },
    handleMouseUp(context: RowEventContext<any>) {
      const { e } = context;
      const { button } = e;
      if (button === 0) {
        methods.hanldeLeftMouseUp(context);
      }
    },
    IsListItemDroppable(index: number): DroppableStatus {
      const moveToDir = tableData.list[index];
      // 不是文件夹不迁移
      if (moveToDir.type !== 'dir') {
        return DroppableStatus.FileTypeNotDroppable;
      }

      // 目标文件夹正在被拖拽，不迁移
      if (tableData.selectedRowKeys.includes(index)) {
        return DroppableStatus.DraggingFileNotDroppable;
      }
      return DroppableStatus.Droppable;
    },
    hanldeLeftMouseUp(context: RowEventContext<any>) {
      const { index, e } = context;
      if (isDragging.value) {
        const moveToDir = tableData.list[index];
        if (methods.IsListItemDroppable(index) !== DroppableStatus.Droppable) {
          return;
        }
        const moveToPath = moveToDir.path.join('/');
        moveFileTask.value = {
          sourceFile: tableData.selectedList,
          moveToPath,
        };
      }
    },
    // 处理鼠标Up在面包屑上（路径中的文件夹）
    handleMouseUpOnBreadCrumb(item: ListBreadcrumb, index?: number) {
      if (!isDragging.value) {
        return;
      }
      const { crumbs } = tableData;
      if (!crumbs) {
        return;
      }
      if (item.path === undefined) {
        return;
      }
      moveFileTask.value = {
        sourceFile: tableData.selectedList,
        moveToPath: item.path,
      };
    },
    handleRowMouseDown(context: RowEventContext<any>) {
      const { index, row, e } = context;
      const { target, button, type } = e;
      isRowMouseDown = true;
      if (type === 'mousedown' && button !== 2) {
        return;
      }
      isMarqueeSelectItem = false; // 右键不触发框选
      methods.handleClickRow(context);
    },
    handleSelectRowsByIndex(value: Array<number>) {
      const selectedRowData: any[] = [];
      value.forEach(index => {
        selectedRowData.push(tableData.list[index]);
      });
      ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_FILTER, selectedRowData); // 误删恢复独有
      tableData.selectedRowKeys = value;
      tableData.selectedList = selectedRowData;
      methods.getShiftStartIndex();
    },
    handleSelectRows(value: Array<String | Number>, { selectedRowData }: any) {
      ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_FILTER, selectedRowData); // 误删恢复独有
      // 点击多选复选框全选，operateShowMap也需置为false
      if (tableData.selectedList.length > 0) {
        vm.$set(operateShowMap, tableData.selectedList[0].name, false);
      }
      tableData.selectedRowKeys = value;
      tableData.selectedList = selectedRowData;
      methods.getShiftStartIndex();
    },
    // 自定义右键callback事件
    rightClickCallback(event: any): boolean {
      const { path } = event;
      // firefox和safari用composedPath
      const parent = path || (event.composedPath && event.composedPath());
      return parent.find(
        (item: HTMLElement) =>
          item.tagName && item.tagName.toLowerCase() === 'tr' && !item.className.includes('disable'),
      );
    },
    // 右键菜单隐藏
    menuShowCallback(bShow: any): void {
      isMenuShow = bShow;
    },
    // 点击更多按钮时选中当前行 并显示下拉菜单
    handleEllipsisClick(row: any) {
      tableData.selectedRowKeys = [row.id];
      tableData.selectedList = [row];
      isActionFromMoreBtn.value = true;
      isDropdownShow.value = true;
    },
    handleEnterRow(context: RowEventContext<any>) {
      if (isShowMask.value && (maskHeight.value > 5 || maskWidth.value > 5)) {
        return;
      }
      const { index, row } = context;
      // 当有下拉菜单显示时 隐藏移入事件
      if (!isDropdownShow.value) {
        hoverIndex.value = index;
        vm.$set(operateShowMap, row.name, true);
      }

      // 处理拖拽文件，检测目前的列表项是否可以drop
      if (isDragging.value) {
        droppableStatus.value = methods.IsListItemDroppable(index);
      }
    },
    handleLeaveRow(context: RowEventContext<any>) {
      const { row } = context;
      if (!isDropdownShow.value) {
        hoverIndex.value = -1;
        vm.$set(operateShowMap, row.name, false);
      }
    },
    onMouseEnterEvent(command: string) {
      switch (command) {
        case 'first':
          isFirstButtonHover.value = true;
          break;
        case 'second':
          isSecondButtonHover.value = true;
          break;
        case 'third':
          isThirdButtonHover.value = true;
          break;
        case 'fourth':
          isFourthButtonHover.value = true;
          break;
        case 'fifth':
          isFifthButtonHover.value = true;
          break;
        case 'sixth':
          isSixthButtonHover.value = true;
          break;
        default:
          break;
      }
    },
    onMouseLeaveEvent(command: string) {
      switch (command) {
        case 'first':
          isFirstButtonHover.value = false;
          break;
        case 'second':
          isSecondButtonHover.value = false;
          break;
        case 'third':
          isThirdButtonHover.value = false;
          break;
        case 'fourth':
          isFourthButtonHover.value = false;
          break;
        case 'fifth':
          isFifthButtonHover.value = false;
          break;
        case 'sixth':
          isSixthButtonHover.value = false;
          break;
        default:
          break;
      }
    },
    onScroll(params: { e: WheelEvent }) {
      isDropdownShow.value = false;
      isFirstButtonHover.value = false;
      isSecondButtonHover.value = false;
      isThirdButtonHover.value = false;
      isFourthButtonHover.value = false;
      isFifthButtonHover.value = false;
      isSixthButtonHover.value = false;
      listRowClassCache = [];
      listRowTagetCache = params.e.target as HTMLElement;
    },
    onClickOutside() {
      isDropdownShow.value = false;
    },
    isContextMenuShow(): boolean {
      return isMenuShow;
    },
    // 框选实现
    resetMask(): void {
      maskLeft.value = 0;
      maskTop.value = 0;
      maskWidth.value = 0;
      maskHeight.value = 0;
    },
    genListRowCache(): void {
      listRowCacheIndex = 999;
      const footerHeight = document.getElementById('file-page__table-pagination')?.getBoundingClientRect().height || 0;
      const tableHeaderRect =
        document
          .getElementById('rootDomForDrawer')
          ?.getElementsByClassName('t-table__header')[0]
          ?.getBoundingClientRect() || null;
      let headerHeight = 0;
      if (tableHeaderRect && tableListRect) {
        headerHeight = tableHeaderRect.bottom - tableListRect.top > 0 ? tableHeaderRect.bottom - tableListRect.top : 0;
      }

      let firstVisibleIndex = 0;

      if (listRowTagetCache) {
        const { scrollTop } = listRowTagetCache;
        firstVisibleIndex = ~~(scrollTop / 56) - 1;
        firstVisibleIndex = firstVisibleIndex > 0 ? firstVisibleIndex : 0; // 第一个可见元素的 index
      }

      for (let index = firstVisibleIndex; index < listRowClass.length; ++index) {
        const rects = listRowClass[index].getClientRects()[0];
        // 头尾15个像素剔除，item的上下留白较多，防止误操作
        if (
          tableListRect !== undefined &&
          tableListRect.top + headerHeight + 15 < rects.bottom &&
          tableListRect.bottom - footerHeight - 15 > rects.top
        ) {
          listRowClassCache.push(rects);
          if (listRowCacheIndex > index) {
            listRowCacheIndex = index;
          }
        }

        if (tableListRect && tableListRect.bottom - footerHeight < rects.top) {
          break;
        }
      }
    },
    collide(rect1: any, rect2: any): boolean {
      const maxX: number = Math.max(rect1.x + rect1.width, rect2.x + rect2.width);
      const maxY: number = Math.max(rect1.y + rect1.height, rect2.y + rect2.height);
      const minX: number = Math.min(rect1.x, rect2.x);
      const minY: number = Math.min(rect1.y, rect2.y);
      if (
        rect2.width === 0 &&
        rect2.height === 0 &&
        rect2.x >= rect1.x &&
        rect2.x <= rect1.x + rect1.width &&
        rect2.y >= rect1.y &&
        rect2.y <= rect1.y + rect1.height
      ) {
        return true;
      }
      if (maxX - minX <= rect1.width + rect2.width && maxY - minY <= rect1.height + rect2.height) {
        return true;
      }
      return false;
    },
    handleMarqueeSelect(): void {
      if (!isShowMask.value) {
        return;
      }
      if (maskHeight.value < 5 && maskWidth.value < 5) {
        // 小于5*5认为是抖动,特别的，如果点击的是空白处不用处理抖动的情况
        return;
      }
      if (domMask === null || domMask === undefined) {
        domMask = document.getElementById('selectMask');
      }
      const rect_select = domMask?.getClientRects()[0] || {
        width: 0,
        height: 0,
      };
      if (listRowClass.length === 0) {
        listRowClass = Array.from(document.querySelectorAll('.listRowClassName'));
      }
      if (listRowClassCache.length === 0) {
        // 更新缓存，用于加速
        this.genListRowCache();
      }
      this.handleSelectRowsByRect(rect_select);
    },
    handleSelectRowsByRect(rect_select: any): void {
      const select_list: Array<number> = [];
      listRowClassCache?.forEach((itemRect: any, index: number) => {
        if (this.collide(itemRect, rect_select)) {
          select_list.push(index + listRowCacheIndex);
        }
      });
      if (select_list.length !== 0) {
        isMarqueeSelectItem = true;
      }
      this.handleSelectRowsByIndex(select_list);
    },
    computeMask(): void {
      if (!isShowMask.value) {
        return;
      }
      maskWidth.value = Math.abs(endX - startX);
      maskHeight.value = Math.abs(endY - startY);
      if (tableListRect !== undefined) {
        maskLeft.value = Math.min(startX, endX) - tableListRect.left;
        maskTop.value = Math.min(startY, endY) - tableListRect.top;
      } else {
        maskLeft.value = Math.min(startX, endX);
        maskTop.value = Math.min(startY, endY);
      }
    },
    mouseMoveComputeMaskThrottledMethod: _.throttle((thisObj: any) => {
      thisObj.computeMask();
    }, 50),
    mouseMoveSelectThrottledMethod: _.throttle((thisObj: any) => {
      thisObj.handleMarqueeSelect();
    }, 150),
    onMouseDownEvent(event: MouseEvent): boolean {
      if (event.button !== 0) return false; // 只响应鼠标左键
      if (isDropdownShow.value || this.isContextMenuShow()) return false;
      // 判断鼠标是否位于表头部分，查找父节点是否有class为t-table__header的节点，如果有则不响应鼠标事件
      let target = event.target as HTMLElement;
      while (target && target !== document.body) {
        if (target.classList.contains('t-table__header')) {
          return false;
        }
        target = target.parentNode as HTMLElement;
      }
      isSelectDownBengin = true;
      isMarqueeSelectItem = false;
      startX = event.clientX;
      startY = event.clientY;
      endX = event.clientX;
      endY = event.clientY;
      return false;
    },
    onMouseMoveEvent(event: MouseEvent): boolean {
      if (dragMouseDownEventContext) {
        return false;
      }
      if (isSelectDownBengin && !isShowMask.value) {
        if (Math.abs(event.clientX - startX) > 5 || Math.abs(event.clientY - startY) > 5) {
          isShowMask.value = true;
          this.resetMask();
          domMask = null;
          listRowClassCache = [];
          listRowTagetCache = undefined;
          listRowClass = [];
          tableListRect = document.getElementById('rootDomForDrawer')?.getBoundingClientRect();
        }
      }
      if (isShowMask.value) {
        endX = event.clientX;
        endY = event.clientY;
        this.mouseMoveComputeMaskThrottledMethod(this);
        this.mouseMoveSelectThrottledMethod(this);
      }
      return true;
    },
    onMouseUpEvent(event: MouseEvent): boolean {
      if (isShowMask.value) {
        this.handleMarqueeSelect();
        isShowMask.value = false;
        this.resetMousePos();
        this.resetMask();
      } else if (Math.abs(event.clientX - startX) < 5 && Math.abs(event.clientY - startY) < 5 && !isRowMouseDown) {
        const { className, parentElement } = event.target as HTMLElement;
        if (
          typeof className === 'string' &&
          !className.includes('file-list-menu-click-detect') &&
          className !== 't-checkbox__input'
        ) {
          // 过滤文件列表item复选框和右键菜单点击
          // 如果没有触发鼠标左键行的动作， 认为点击了空白处逻辑
          // 点击空白处时，若之前有选中，需将operateShowMap置为false
          if (tableData.selectedList.length > 0) {
            vm.$set(operateShowMap, tableData.selectedList[0].name, false);
          }
          tableData.selectedRowKeys = [];
          tableData.selectedList = [];
        }
      }
      isSelectDownBengin = false;
      isRowMouseDown = false;
      return true;
    },
    resetMousePos(): void {
      startX = 0;
      startY = 0;
      endX = 0;
      endY = 0;
    },
    handleShiftClick(index: number): boolean {
      const start_index = Math.min(shift_start_index, index);
      const end_index = Math.max(shift_start_index, index);
      const selectedRowData: any[] = [];
      const selectedRowKey: any[] = [];
      for (let i = start_index; i <= end_index; i++) {
        selectedRowKey.push(i);
      }
      selectedRowKey.forEach(index => {
        selectedRowData.push(tableData.list[index]);
      });
      ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_FILTER, selectedRowData); // 误删恢复独有
      tableData.selectedRowKeys = selectedRowKey;
      tableData.selectedList = selectedRowData;
      return true;
    },
    getShiftStartIndex(): void {
      // 根据选中item来计算
      let min_index = -1;
      tableData.selectedRowKeys.forEach((item, index) => {
        if (min_index === -1 || min_index > item) {
          min_index = item;
        }
      });
      if (min_index !== -1) {
        shift_start_index = min_index;
      }
    },
    handleCtrlClick(selectedIndex: number, row: any): void {
      if (selectedIndex === -1) {
        const temp_selectedRowKeys = tableData.selectedRowKeys;
        const temp_selectedList = tableData.selectedList;
        temp_selectedRowKeys.push(row.id);
        temp_selectedList.push(row);
        tableData.selectedRowKeys = _.cloneDeep(temp_selectedRowKeys);
        tableData.selectedList = _.cloneDeep(temp_selectedList);
      } else {
        tableData.selectedRowKeys = tableData.selectedRowKeys.filter(id => id !== row.id);
        tableData.selectedList = tableData.selectedList.filter(item => item.id !== row.id);
      }
      methods.getShiftStartIndex();
    },
    // 处理Esc按下
    handleEscClick() {
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
    },
  };
  return {
    operateShowMap,
    isDropdownShow,
    isActionFromMoreBtn,
    isFirstButtonHover,
    isSecondButtonHover,
    isThirdButtonHover,
    isFourthButtonHover,
    isFifthButtonHover,
    isSixthButtonHover,
    hoverIndex,
    spaceId,
    methods,
    isShowMask,
    maskWidth,
    maskHeight,
    maskLeft,
    maskTop,
    moveFileTask,
    isDragging,
    droppableStatus,
  };
}
export { DroppableStatus };
